import { HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpHeaderConstants } from '@r1-compass-library/constants';
import { Play, PlayConfig } from '@r1-compass-library/models/play/play.model';
import { PlaySearch } from '@r1-compass-library/models/search/play-search.model';
import { catchError, finalize, Observable, of, retry } from 'rxjs';
import { Environment } from '@r1-compass-library/environments/environment';
import { AllCharacterHttpParamsEncoder } from '@r1-compass-library/utilities';
import { AssetRestService } from './rest/asset-rest.service';

@Injectable({ providedIn: 'root' })
export class PlayService extends AssetRestService<Play, PlaySearch> {
    protected override BASE_URL: string = Environment.baseUrl + '/frontend/plays';
    protected override TARGET_OBJECT = 'play';

    processNamePrefix = 'PlayService';

    patchPlays(
        search: PlaySearch & { ids: number[] },
        data: Partial<Play>,
        additionalHeaders: any = {}
    ): Observable<Play[]> {
        if (!search.ids || search.ids.length === 0) {
            return of([]);
        }

        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
            params: new HttpParams({ encoder: new AllCharacterHttpParamsEncoder() }),
        };

        this.setSearchParams(search, httpOptions);

        const process = `${this.processNamePrefix}.patchPlays`;
        this.spinnerService.addProcess(process, 'Updating plays...');
        return this.http.patch<Play[]>(this.BASE_URL, data, httpOptions).pipe(
            catchError(() => of<Play[]>([])),
            finalize(() => this.spinnerService.completeProcess(process))
        );
    }

    getByIamDivisionId(iamDivisionId: number, additionalHeaders: any = {}): Observable<Play> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('iamDivisionId', iamDivisionId),
        };

        const process = 'PlayService.getByIamDivisionId';
        this.spinnerService.addProcess(process, 'Getting play...');
        return this.http.get<Play>(`${this.BASE_URL}`, httpOptions).pipe(
            retry(0),
            catchError(() => {
                return of<Play>();
            }),
            finalize(() => this.spinnerService.completeProcess(process))
        );
    }

    updateData(id: number, data: Partial<PlayConfig>, additionalHeaders: any = {}): Observable<Play> {
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
        };
        const process = 'PlayService.updateData';
        this.spinnerService.addProcess(process, 'Updating play...');
        return this.http.patch<Play>(`${this.BASE_URL}/${id}/data?fieldName=config`, data, httpOptions).pipe(
            catchError(error => {
                return of<Play>();
            }),
            finalize(() => this.spinnerService.completeProcess(process))
        );
    }

    updateExpiryData(id: number, additionalHeaders: any = {}) {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.http.post<void>(`${this.BASE_URL}/${id}/expiration`, {}, httpOptions).pipe(
            retry(0),
            catchError(() => {
                return of<void>();
            })
        );
    }

    runNow(id: number, userId: number, additionalHeaders: any = {}): Observable<PlayOnDemandExecutionResponse> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        const process = 'PlayService.runNow';
        this.spinnerService.addProcess(process, 'Running play on-demand...');

        return this.http
            .post<PlayOnDemandExecutionResponse>(`${this.BASE_URL}/${id}/executions`, { userId: userId }, httpOptions)
            .pipe(
                retry(0),
                catchError((err: HttpErrorResponse) => {
                    console.error('On-demand play execution request failed', err);
                    throw err;
                }),
                finalize(() => this.spinnerService.completeProcess(process))
            );
    }

    removeAdditionalField(additionalFieldId: string, divisionCode: string, additionalHeaders: any = {}) {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        const process = `${this.processNamePrefix}.removeAdditionalField`;
        this.spinnerService.addProcess(process, 'Deleting additional fields');

        return this.http
            .put<void>(`${this.BASE_URL}/remove-additional-field`, { additionalFieldId, divisionCode }, httpOptions)
            .pipe(
                retry(0),
                catchError(() => {
                    return of<void>();
                }),
                finalize(() => this.spinnerService.completeProcess(process))
            );
    }
}

export type PlayOnDemandExecutionResponse = {
    success: boolean;
    errorMessage: string;
};
